import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { map, switchMap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DirtyComponent } from '../components/dirty.component';
import { ErrorMessageComponent } from '../../shared/presentationals/messages/error-message/error-message.component';
import { SuccessMessageComponent } from '../../shared/presentationals/messages/success-message/success-message.component';
import { ConfirmMessageComponent } from '../../shared/presentationals/messages/confirm-message/confirm-message.component';
import { BaseComponent } from '../components/base.component';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService
  ) {
  }

  openError(message: string): MatSnackBarRef<ErrorMessageComponent> {
    return this.snackBar.openFromComponent(ErrorMessageComponent, {
      horizontalPosition: 'end',
      data: message
    });
  }

  openErrorTranslationObservable(messageKey: string, params?: object): Observable<MatSnackBarRef<ErrorMessageComponent>> {
    return this.translateService.get(messageKey, params)
      .pipe(map(translation => {
        return this.openError(translation);
      }));
  } 
  
  openErrorTranslation(messageKey: string, params?: object): void {
     this.openErrorTranslationObservable(messageKey, params)
     .subscribe()
     .unsubscribe();
  }

  openSuccess(message: string, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {
    return this.snackBar.openFromComponent(SuccessMessageComponent, {
      horizontalPosition: 'end',
      data: message,
      duration: duration
    });
  }

  openSuccessTranslationObservable(messageKey: string, params?: object): Observable<MatSnackBarRef<SuccessMessageComponent>> {
    return this.translateService.get(messageKey, params)
      .pipe(map(translation => {
        return this.openSuccess(translation);
      }));
  } 
  
  openSuccessTranslation(messageKey: string, params?: object): void {
     this.openSuccessTranslationObservable(messageKey, params)
     .subscribe()
     .unsubscribe();
  }

  openSaveSuccess(): void {
    this.openSuccessTranslation('Messages.SaveSuccess');
  }

  openDeleteSuccess(): void {
    this.openSuccessTranslation('Messages.DeleteSuccess');
  }

  openConfirm(message: string, primaryButtonLabel: string | null = null, secondaryButtonLabel: string | null = null): Observable<MatSnackBarRef<ConfirmMessageComponent>> {
    if (!primaryButtonLabel) {
      primaryButtonLabel = 'Common.Ok';
    }

    if (!secondaryButtonLabel) {
      secondaryButtonLabel = 'Common.Cancel';
    }

    return this.translateService.get([primaryButtonLabel, secondaryButtonLabel])
      .pipe(map(translations => {
        return this.snackBar.openFromComponent(ConfirmMessageComponent, {
          horizontalPosition: 'end',
          data: {
            message: message,
            primaryButtonLabel: translations[primaryButtonLabel!],
            secondaryButtonLabel: translations[secondaryButtonLabel!],
          }
        });
      }));  
  }

  openDeleteConfirm(component: BaseComponent, onConfirm: () => void) {
    this.translateService.get('Messages.DeleteConfirmation')
      .subscribe(message => {
        this.openDefaultConfirm(component, message, 'Common.Delete', null, onConfirm);
      }).unsubscribe();
  }

  openDefaultConfirm(component: BaseComponent,
    message: string,
    primaryButtonLabel: string | null = null,
    secondaryButtonLabel: string | null = null,
    onConfirm: () => void) {
    this.openConfirm(message, primaryButtonLabel, secondaryButtonLabel)
      .pipe(switchMap(snackBarRef => {
        return snackBarRef
          .afterDismissed()
          .pipe(map(() => snackBarRef.instance.isConfirmed));
      }))
      .unsubscribeOnDestroy(component)
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          onConfirm();
        }
      });
  }

  openDirty(component: DirtyComponent): Observable<boolean> {
    return component.isDirty.pipe(
      switchMap(isDirty => {
        if (isDirty) {
          return this.translateService.get('Messages.CancelConfirmation')
            .pipe(switchMap(translation => {
              return this.openConfirm(translation)
                .pipe(switchMap(snackBarRef => {
                  return snackBarRef
                    .afterDismissed()
                    .pipe(map(() => {
                        return snackBarRef.instance.isConfirmed;
                      }));
                }));
            }));
        }

        return of(true);
      }));
  }
}
